import { Component, EventEmitter, HostListener, Input, OnInit, Output, OnChanges, SimpleChanges } from "@angular/core";
import {
  WsNavigatorService,
  MenuItem,
} from "../../services/ws-navigator.service"; // Adjust the path as necessary
import { NavigationEnd, Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { filter, first } from "rxjs/operators";
import { AccountsService } from "@services/accounts.service";
import { combineLatest } from "rxjs";
import { SidenavContentService } from "../../services/sidenav-content.service";
import { AdminLoginAsWineryService } from "@services/dws/admin-login-as-winery.service";
import { AuthenticateAdminService } from "@services/dws/authenticate-admin.service";
import { PermissionResolverService } from "@services/permissions/permission-resolver.service";
@Component({
  selector: "ws-menu",
  templateUrl: "./ws-menu.component.html",
  styleUrls: ["./ws-menu.component.scss"],
})
export class WsMenuComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean;
  @Input() dwsPro: boolean = false;
  @Input() versionText: string = "";
  @Input() versionPostfix: string | undefined = undefined;
  @Output()
  isOpenChange: EventEmitter<boolean> = new EventEmitter();
  @HostListener("window:resize")
  onResize(): void {
    this.checkScreenWidth();
  }
  isSmallScreen: boolean = false;
  menuState: { [key: string]: boolean } = {}; // Tracks open/close state for each section
  menuItems: MenuItem[] = [];
  isAdminAsWinery: boolean = false;
  currentWineryName: string = '';
  adminMenuItems: any[] = [];
  isAdminMenuExpanded: boolean = false;
  showOnlyAdminMenu: boolean = false;
  /** Account with SUPERADMIN system permission (winery JWT, no legacy admin token). */
  showAccountSuperAdminPanel: boolean = false;
  /** Grant Super Admin to others: gated on the SUPERADMIN permission, not session mode. */
  canGrantSuperAdmin: boolean = false;
  adminModule: boolean = false;

  closeAfterNextNavigation: boolean = false;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private http: HttpClient,
    private wsNavigationService: WsNavigatorService,
    private accountsService: AccountsService,
    private sidenavContentService: SidenavContentService,
    private adminLoginAsWineryService: AdminLoginAsWineryService,
    private adminAccountService: AuthenticateAdminService,
    private permissionResolver: PermissionResolverService
  ) {
    this.isOpen = localStorage.getItem("ws-menu-open") != "false";
  }
  ngOnInit() {
    // create first time menu items
    this.menuItems = this.wsNavigationService.menuItems;
    this.updateMenuItemsDisabledState();

    // Subscribe to Router events for NavigationEnd
    this.wsNavigationService.navigationState$.subscribe((state) => {
      this.menuItems = state.menuItems;
      this.updateMenuItemsDisabledState();
      this.updateMenuState();
    });

    this.accountsService.cachedPermissions$.subscribe(() => {
      this.updateMenuItemsDisabledState();
    });

    this.accountsService.login$.subscribe(() => {
      this.updateMenuItemsDisabledState();
    });

    // Monitor admin access status and winery name
    combineLatest([
      this.accountsService.authorization$,
      this.accountsService.company$,
      this.accountsService.login$,
      this.adminAccountService.authorization$
    ]).subscribe(([auth, company, login, adminAuth]) => {
      this.isAdminAsWinery = !!(auth?.is_admin && auth?.is_winery);
      this.currentWineryName = company?.name || '';
      this.adminModule = !!adminAuth; // Set adminModule based on admin authentication
      const accountSuperAdmin = this.accountsService.hasActiveSuperAdminSession(login);
      this.showAccountSuperAdminPanel = accountSuperAdmin;
      this.canGrantSuperAdmin = this.accountsService.hasSuperAdminPermission(login);

      // Check if we're in login-as-winery route as admin
      const isInAdminSection = this.router.url.startsWith('/admin/');
      const isAdminNormal = !!(adminAuth?.is_admin && !auth?.is_winery);
      const isSuperAdmin = this.accountsService.hasActiveSuperAdminSession(login);

      // Show only admin menu when in any admin section as privileged admin
      this.showOnlyAdminMenu = isInAdminSection && (isAdminNormal || isSuperAdmin);


      // Auto-expand admin menu when in showOnlyAdminMenu mode
      if (this.showOnlyAdminMenu) {
        this.isAdminMenuExpanded = true;
      }

      // Populate admin menu items
      if (this.isAdminAsWinery || (isAdminNormal && isInAdminSection) || accountSuperAdmin) {
        this.adminMenuItems = [
          {
            label: 'Dashboard Admin',
            route: '/admin/login-as-winery',
            icon: 'admin_panel_settings'
          },
          {
            label: 'Sign-IT Onboarding',
            route: '/admin/signit-onboarding',
            icon: 'receipt'
          },
          {
            label: 'Contact Import ADMIN',
            route: '/admin/contact-import-jobs',
            icon: 'import_contacts'
          },
          {
            label: 'Homepage Setting',
            route: '/admin/homepage-settings',
            icon: 'dashboard'
          },
          {
            label: 'Templates BASE',
            route: '/admin/transactional-emails',
            icon: 'email'
          },
          {
            label: 'Reservation Logs',
            route: '/admin/reservation-logs',
            icon: 'bug_report'
          },
          {
            label: 'Notification Manager',
            route: '/admin/notifications-manager-standalone',
            icon: 'notifications'
          },
          {
            label: 'Notification Manager Old',
            route: '/admin/notifications-manager',
            icon: 'notifications'
          },
          {
            label: 'Avalaibilities Checker',
            route: '/admin/availabilities-checker',
            icon: 'search'
          },
          {
            label: 'Resend Emails',
            route: '/admin/resend-email',
            icon: 'send'
          }
        ];
      } else {
        this.adminMenuItems = [];
      }
      if (this.canGrantSuperAdmin) {
        this.adminMenuItems.push({
          label: 'Super Admin users',
          route: '/super-admin-grant',
          icon: 'shield',
        });
      }
    });

    // Listen to route changes to update showOnlyAdminMenu
    this.router.events.subscribe((event) => {
      this.updateAdminMenuVisibility();
    });
  }

  private updateAdminMenuVisibility() {
    const isInAdminSection = this.router.url.startsWith('/admin/');
    combineLatest([
      this.accountsService.authorization$,
      this.accountsService.login$,
      this.adminAccountService.authorization$
    ]).subscribe(([auth, login, adminAuth]) => {
      const isAdminNormal = !!(adminAuth?.is_admin && !auth?.is_winery);
      const isSuperAdmin = this.accountsService.hasActiveSuperAdminSession(login);
      this.showOnlyAdminMenu = isInAdminSection && (isAdminNormal || isSuperAdmin);

      // Auto-expand admin menu when in showOnlyAdminMenu mode
      if (this.showOnlyAdminMenu) {
        this.isAdminMenuExpanded = true;
      }
    });
  }

  logoutFromAdmin(event: Event) {
    event.stopPropagation();

    this.accountsService.login$.pipe(first()).subscribe((login) => {
      const isSuperAdminSession = this.accountsService.hasActiveSuperAdminSession(login);

      if (this.isAdminAsWinery || isSuperAdminSession) {
        this.accountsService.logout();
        this.adminAccountService.logout();
        this.adminLoginAsWineryService.clearOriginalAdminToken();
        void this.router.navigate(['/login']);
        return;
      }

      if (this.showOnlyAdminMenu) {
        this.adminAccountService.logout();
        void this.router.navigate(['/login']);
        return;
      }

      this.adminAccountService.logout();
      void this.router.navigate(['/login']);
    });
  }

  async goToDashboardAdmin() {
    this.sidenavContentService.closeContent();
    await this.navigate('/admin/login-as-winery');
  }

  get isPublicRoute() {
    return (
      this.router.url === "/login" ||
      this.router.url.startsWith("/registration") ||
      this.router.url.startsWith("/login") ||
      this.router.url.startsWith("/unsubscribe") ||
      this.router.url.startsWith("/reset-password") ||
      this.router.url.startsWith("/set-password") ||
      this.router.url.startsWith("/mfa-reenroll") ||
      this.router.url.startsWith("/privacy")
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // Watch for changes to dwsPro input and update menu items accordingly
    if (changes['dwsPro'] && !changes['dwsPro'].firstChange) {
      this.updateMenuItemsDisabledState();
    }
  }

  private updateMenuItemsDisabledState() {
    const permissionsPending = !this.accountsService.hasCachedPermissions();

    this.menuItems.forEach((item) => {
      if (permissionsPending) {
        item.disabled = true;
        item.subItems?.forEach((subItem) => {
          subItem.disabled = true;
        });
        this.menuState[item.key] = false;
        return;
      }

      if (item.subItems?.length) {
        item.subItems.forEach((subItem) => {
          subItem.disabled =
            this.isDisabledByPermission(subItem) ||
            this.isDisabledBySubscription(subItem);
        });

        const hasEnabledSubItem = item.subItems.some((subItem) => !subItem.disabled);
        item.disabled =
          this.isDisabledBySubscription(item) ||
          this.isDisabledByPermission(item) ||
          !hasEnabledSubItem;

        if (item.disabled) {
          this.menuState[item.key] = false;
        }
      } else {
        item.disabled =
          this.isDisabledByPermission(item) ||
          this.isDisabledBySubscription(item);
      }
    });
  }

  /** Pro-only sections depend on winery subscription (`dwsPro`), not user permissions. */
  private isDisabledBySubscription(entry: { disabledIfNotPro?: boolean }): boolean {
    return !!(entry.disabledIfNotPro && !this.dwsPro);
  }

  private isDisabledByPermission(entry: {
    permission?: string;
    permissionSectionBase?: string;
  }): boolean {
    return !this.canAccessMenuEntry(entry);
  }

  sectionTooltip(item: MenuItem): string | null {
    const denied = this.permissionDeniedTooltip(item);
    if (denied) {
      return denied;
    }
    if (!this.isOpen) {
      return this.translate.instant(item.label);
    }
    return null;
  }

  subItemTooltip(subItem: {
    permission?: string;
    permissionSectionBase?: string;
    disabled?: boolean;
    disabledIfNotPro?: boolean;
  }): string | null {
    return this.permissionDeniedTooltip(subItem);
  }

  private permissionDeniedTooltip(entry: {
    permission?: string;
    permissionSectionBase?: string;
    disabled?: boolean;
    disabledIfNotPro?: boolean;
  }): string | null {
    if (!this.accountsService.hasCachedPermissions()) {
      return null;
    }
    if (this.isDisabledBySubscription(entry)) {
      return null;
    }
    if (!entry.disabled && !this.isDisabledByPermission(entry)) {
      return null;
    }
    return this.translate.instant("You do not have permission for this action");
  }

  private canAccessMenuEntry(entry: {
    permission?: string;
    permissionSectionBase?: string;
  }): boolean {
    if (entry.permissionSectionBase) {
      return this.permissionResolver.canViewSection(entry.permissionSectionBase);
    }
    if (entry.permission) {
      return this.accountsService.hasPermission(entry.permission);
    }
    return true;
  }

  checkScreenWidth() {
    const currentWidth = window.innerWidth;
    this.isSmallScreen = currentWidth < 1024;
    if (this.isSmallScreen) {
      this.isOpen = false;
      this.isOpenChange.emit(this.isOpen);
    } else {
      this.isOpen = localStorage.getItem("ws-menu-open") != "false";
      this.isOpenChange.emit(this.isOpen);
    }
  }

  updateMenuState(): void {
    // Initialize menuState to keep track of open/close state for each section
    this.menuItems.forEach((item) => {
      // Initialize state for the main item (without subItems)
      this.menuState[item.key] = this.isLastSection(item);
    });
  }

  closeMenu() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
    localStorage.setItem("ws-menu-open", "false");
  }

  public close() {
    this.closeMenu();
  }

  public toggle() {
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);
    if (!this.isOpen) {
      //this.menuState = {}; // Close all subsections ?
    }
    localStorage.setItem("ws-menu-open", this.isOpen ? "true" : "false");
  }

  async navigate(route: string) {
    if (this.isOpen && this.closeAfterNextNavigation) {
      this.closeAfterNextNavigation = false;
      this.toggle();
    }
    await this.router.navigate([route]);
  }

  toggleSection(key: string) {
    const item = this.menuItems.find((entry) => entry.key === key);
    if (item?.disabled) {
      return;
    }
    if (!this.isOpen) {
      this.toggle();
      this.closeAfterNextNavigation = true;
      this.menuState[key] = true;
    } else {
      this.menuState[key] = !this.menuState[key];
    }
  }

  toggleAdminMenu() {
    this.isAdminMenuExpanded = !this.isAdminMenuExpanded;
  }


  async goTo(route: string) {
    if (route.startsWith('/admin/') && this.showOnlyAdminMenu) {
      // If we're in admin-only mode and navigating to admin routes, 
      // keep the menu open and navigate normally
      this.sidenavContentService.closeContent();
      await this.navigate(route);
    } else if (this.isProOnlyRoute(route) || this.isPermissionDeniedRoute(route)) {
      return;
    } else {
      // Close any open sidenav content when navigating to other sections
      this.sidenavContentService.closeContent();
      await this.navigate(route);
    }
  }

  private isProOnlyRoute(route: string): boolean {
    if (this.dwsPro) {
      return false;
    }
    const normalized = route.startsWith('/') ? route.slice(1) : route;
    for (const item of this.menuItems) {
      if (
        item.disabledIfNotPro &&
        this.matchesMenuRoute(item.route, route, normalized)
      ) {
        return true;
      }
      for (const subItem of item.subItems ?? []) {
        if (
          subItem.disabledIfNotPro &&
          this.matchesMenuRoute(subItem.route, route, normalized)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private isPermissionDeniedRoute(route: string): boolean {
    const normalized = route.startsWith('/') ? route.slice(1) : route;
    for (const item of this.menuItems) {
      if (
        this.matchesMenuRoute(item.route, route, normalized) &&
        this.isDisabledByPermission(item)
      ) {
        return true;
      }
      for (const subItem of item.subItems ?? []) {
        if (
          this.matchesMenuRoute(subItem.route, route, normalized) &&
          this.isDisabledByPermission(subItem)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private matchesMenuRoute(menuRoute: string | undefined, route: string, normalized: string): boolean {
    if (!menuRoute) {
      return false;
    }
    return menuRoute === normalized || `/${menuRoute}` === route || menuRoute === route;
  }

  isLastSection(item: MenuItem): boolean {
    return this.wsNavigationService.isLastSection(item);
  }
}
